<template>
  <div>
    <!-- Quests Table Section -->
    <el-row v-if="!selectedQuest">
      <el-col :span="24">
        <el-card class="data" v-loading="loading">
          <div class="data-title flex-space-between" slot="header">
            <div>
              <p v-if="cult">Quests for {{ cult.name }}</p>
              <p v-else>Quests</p>
            </div>
            <el-button type="primary" @click="goBack">Back to Cults</el-button>
          </div>
          <div v-if="cult" class="cult-info mb20">
            <h3>Cult Information</h3>
            <div class="cult-details">
              <p><strong>Expiration Time:</strong> {{ cult.expiration_time | datetime }}</p>
              <p><strong>XP:</strong> {{ cult.xp }}</p>
            </div>
          </div>
          <el-table 
            :data="quests"
            :default-sort="{ prop: order_column, order: order_type }"
            class="w100"
            style="width: 100%"
            stripe
            @sort-change="sort"
          >
            <el-table-column prop="id" label="ID" sortable></el-table-column>
            <el-table-column prop="name" label="Name" sortable></el-table-column>
            <el-table-column prop="budget" label="Budget" sortable>
              <template slot-scope="scope">
                {{ scope.row.budget }}
              </template>
            </el-table-column>
            <el-table-column prop="remaining_budget" label="Remaining Budget" sortable>
              <template slot-scope="scope">
                {{ scope.row.remaining_budget }}
              </template>
            </el-table-column>
            <el-table-column prop="task_count" label="Task Count" sortable>
              <template slot-scope="scope">
                {{ scope.row.task_count }}
              </template>
            </el-table-column>
            <el-table-column prop="submission_count" label="Submission Count" sortable>
              <template slot-scope="scope">
                {{ scope.row.submission_count }}
              </template>
            </el-table-column>
            <el-table-column prop="is_published" label="Published" sortable>
              <template slot-scope="scope">
                <el-tag :type="scope.row.is_published ? 'success' : 'danger'">{{ scope.row.is_published ? 'YES' : 'NO' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="start_date" label="Start Date" sortable>
              <template slot-scope="scope">
                {{ scope.row.start_date | datetime }}
              </template>
            </el-table-column>
            <el-table-column prop="end_date" label="End Date" sortable>
              <template slot-scope="scope">
                {{ scope.row.end_date | datetime }}
              </template>
            </el-table-column>
            <el-table-column label="Status" sortable>
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row)">
                  {{ getStatusText(scope.row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="has_winners" label="Winners selected" sortable align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.has_winners ? 'success' : 'danger'">{{ scope.row.has_winners ? 'YES' : 'NO' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Tasks" align="center">
              <template slot-scope="scope">
                <div style="display: flex; justify-content: center;">
                  <el-tooltip v-if="!scope.row.has_winners && scope.row.ended" content="Pick Winners" placement="top">
                    <el-button type="info" circle size="small" icon="el-icon-trophy"
                    @click="pickWinners({ questId: scope.row.id, cultId: cult.id })"></el-button>
                  </el-tooltip>
                  <el-tooltip content="View Tasks" placement="top">
                    <el-button type="danger" circle size="small" icon="el-icon-view" @click="showTasks(scope.row)"></el-button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="float-right mt20"
            background
            @size-change="sizeChange"
            @current-change="currentChange"
            :current-page.sync="page"
            :page-sizes="[5, 10, 25, 50, 100]"
            :page-size="page_size"
            :page-count="page_count"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
          ></el-pagination>
        </el-card>
      </el-col>
    </el-row>

    <!-- Quest Tasks Section -->
    <el-row v-else>
      <el-col :span="24">
        <el-card class="data">
          <div class="data-title flex-space-between" slot="header">
            <div>
              <p>Tasks for {{ selectedQuest.name }}</p>
            </div>
            <el-button type="primary" @click="selectedQuest = null">Close</el-button>
          </div>
          <el-table :data="selectedQuest.tasks" stripe class="w100" style="width: 100%">
            <el-table-column prop="id" label="ID"></el-table-column>
            <el-table-column prop="name" label="Name"></el-table-column>
            <el-table-column label="Task Type">
              <template slot-scope="scope">
                {{ scope.row.task_type.name }}
              </template>
            </el-table-column>
            <el-table-column prop="reward_xp" label="XP Reward"></el-table-column>
            <el-table-column prop="reward_fiat" label="Fiat Reward"></el-table-column>
            <el-table-column prop="target_url" label="Target URL"></el-table-column>
            <el-table-column prop="created_at" label="Created At">
              <template slot-scope="scope">
                {{ scope.row.created_at | datetime }}
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  data() {
    return {
      selectedQuest: null
    }
  },

  computed: {
    ...mapState('quests', [
      'quests',
      'cult',
      'loading',
      'page',
      'page_size',
      'total',
      'page_count',
      'order_column',
      'order_type'
    ]),
    cultId() {
      return this.$route.params.cult_id
    }
  },

  methods: {
    ...mapActions('quests', ['readQuests', 'pickWinners']),
    ...mapMutations('quests', [
      'setPage',
      'setPageSize',
      'setOrdering'
    ]),

    sizeChange(page_size) {
      this.setPageSize(page_size)
      this.fetchData()
    },

    currentChange(page) {
      this.setPage(page)
      this.fetchData()
    },

    sort(d) {
      this.setOrdering({
        column: d.prop,
        type: d.order === 'ascending' ? 'asc' : 'desc'
      })
      this.fetchData()
    },

    fetchData() {
      this.readQuests(this.cultId)
    },

    goBack() {
      this.$router.push('/cults')
    },

    showTasks(quest) {
      this.selectedQuest = quest
    },

    isScheduled(startDate) {
      if (!startDate) return false
      const now = new Date()
      const start = new Date(startDate)
      return start > now
    },

    getStatusType(quest) {
      if (quest.deleted_at !== null) {
        return 'custom'
      } else if (quest.ended) {
        return 'danger'
      } else if (!quest.is_published) {
        return 'warning'
      } else if (this.isScheduled(quest.start_date) && quest.is_published) {
        return 'success'
      } else {
        return 'success'
      }
    },

    getStatusText(quest) {
      if (quest.deleted_at != null) {
        return 'DELETED'
      } else if (quest.ended) {
        return 'EXPIRED'
      } else if (!quest.is_published) {
        return 'NOT PUBLISHED'
      } else if (this.isScheduled(quest.start_date) && quest.is_published) {
        return 'SCHEDULED'
      } else {
        return 'ACTIVE'
      }
    }
  },

  created() {
    this.fetchData()
  },

  beforeRouteLeave(to, from, next) {
    this.$store.commit('quests/reset')
    next()
  }
}
</script>

<style scoped>
.el-tag--custom {
  color: #FFFFFF;
  background-color: #d06e6e;
  border-color: #d06e6e;
  font-weight: bold;
}
.data-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mr5 {
  margin-right: 5px;
}

.mt20 {
  margin-top: 20px;
}

.float-right {
  float: right;
}

.w100 {
  width: 100%;
}

.mb20 {
  margin-bottom: 20px;
}

.cult-info {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
}

.cult-details {
  display: flex;
  gap: 20px;
}
</style>
